// Finance build — the Income Overview headline + summary split. A whole-book
// money roll-up over the income ledger for the selected window:
//   - a headline "Phoenix-retained earnings" = retained received + VFI received
//     (VFI called out as its own contributor),
//   - the four recognized-event buckets (retained / outflow / pass-through /
//     servicer), each as received-of-expected with a recognized-event variance.
// Read-only. The variance is the recognized-event gap, never outstanding
// receivables (see the cost-recovery cross-link on the page).

import { Card } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import type { IncomeOverviewSummaryDto } from "@/api/portfolioAdmin/incomeOverviewApi";

interface IncomeSummarySplitProps {
  summary: IncomeOverviewSummaryDto;
}

export function IncomeSummarySplit({ summary }: IncomeSummarySplitProps) {
  const retainedEarnings = summary.retainedReceived + summary.vfiReceived;

  return (
    <div className="flex flex-col gap-space-4" data-testid="income-overview-summary">
      <Card dark data-testid="income-overview-headline">
        <div className="font-display text-label font-semibold uppercase tracking-label text-gold">
          Phoenix-retained earnings
        </div>
        <div className="mt-space-2 font-display text-h1 font-extrabold tabular-nums">
          {formatCurrency(retainedEarnings)}
        </div>
        <div className="mt-space-2 text-small text-white-pure/70">
          Retained income {formatCurrency(summary.retainedReceived)} + VFI{" "}
          <span data-testid="income-overview-vfi">
            {formatCurrency(summary.vfiReceived)}
          </span>{" "}
          received this window. {summary.eventCount} recognized event
          {summary.eventCount === 1 ? "" : "s"}.
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-space-3 sm:grid-cols-2 xl:grid-cols-4">
        <BucketTile
          label="Retained"
          expected={summary.retainedExpected}
          received={summary.retainedReceived}
          testId="income-bucket-retained"
        />
        <BucketTile
          label="Outflow"
          expected={summary.outflowExpected}
          received={summary.outflowReceived}
          testId="income-bucket-outflow"
        />
        <BucketTile
          label="Pass-through"
          expected={summary.passThroughExpected}
          received={summary.passThroughReceived}
          testId="income-bucket-passthrough"
        />
        <BucketTile
          label="Servicer"
          expected={summary.servicerExpected}
          received={summary.servicerReceived}
          testId="income-bucket-servicer"
        />
      </div>
    </div>
  );
}

function BucketTile({
  label,
  expected,
  received,
  testId,
}: {
  label: string;
  expected: number;
  received: number;
  testId: string;
}) {
  const variance = expected - received;
  return (
    <Card className="p-space-4" data-testid={testId}>
      <div className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
        {label}
      </div>
      <div className="mt-space-1 font-display text-h3 font-bold text-onyx tabular-nums">
        {formatCurrency(received)}
      </div>
      <div className="text-small text-gray-mid">
        of {formatCurrency(expected)} expected
      </div>
      <div
        className={
          Math.abs(variance) >= 0.005
            ? "mt-space-1 text-caption tabular-nums text-status-error"
            : "mt-space-1 text-caption tabular-nums text-gray-mid"
        }
      >
        Variance {formatCurrency(variance)}
      </div>
    </Card>
  );
}
